import type { AppError } from 'lib/api';
import datadogActions from './index';

export type CabinetPlatform = 'facebook' | 'tiktok';

export type CabinetEntity =
  | 'flow'
  | 'account'
  | 'creative_text'
  | 'go_to_test'
  | 'facebook_page'
  | 'user'
  | 'partnership_ad_tag';

export type CabinetMode = 'create' | 'edit' | 'delete';

type CabinetContext = Record<string, string | number | boolean | undefined>;

const buildKey = (
  platform: CabinetPlatform,
  entity: CabinetEntity,
  suffix: string,
) => `cabinet_${platform}_${entity}_${suffix}`;

export const cabinetDatadogSources = {
  query: (platform: CabinetPlatform, entity: CabinetEntity) =>
    buildKey(platform, entity, 'query'),
  detailsQuery: (platform: CabinetPlatform, entity: CabinetEntity) =>
    buildKey(platform, entity, 'details_query'),
  mutationError: (
    platform: CabinetPlatform,
    entity: CabinetEntity,
    mode: CabinetMode,
  ) => buildKey(platform, entity, `${mode}_error`),
};

export const cabinetDatadogActions = {
  submit: (platform: CabinetPlatform, entity: CabinetEntity, mode: CabinetMode) =>
    buildKey(platform, entity, `${mode}_submit`),
  success: (platform: CabinetPlatform, entity: CabinetEntity, mode: CabinetMode) =>
    buildKey(platform, entity, `${mode}_success`),
};

const normalizeError = (error: unknown): Error =>
  error instanceof Error ? error : new Error(String(error));

export const trackCabinetQueryError = (
  error: unknown,
  platform: CabinetPlatform,
  entity: CabinetEntity,
  context?: CabinetContext,
  variant: 'query' | 'details_query' = 'query',
) => {
  const source =
    variant === 'details_query'
      ? cabinetDatadogSources.detailsQuery(platform, entity)
      : cabinetDatadogSources.query(platform, entity);

  datadogActions.trackDatadogError(normalizeError(error), {
    source,
    ...context,
  });
};

export const trackCabinetSubmitAttempt = (
  platform: CabinetPlatform,
  entity: CabinetEntity,
  mode: CabinetMode,
  context?: CabinetContext,
) => {
  datadogActions.trackDatadogAction(
    cabinetDatadogActions.submit(platform, entity, mode),
    context,
  );
};

export const trackCabinetSubmitSuccess = (
  platform: CabinetPlatform,
  entity: CabinetEntity,
  mode: CabinetMode,
  context?: CabinetContext,
) => {
  datadogActions.trackDatadogAction(
    cabinetDatadogActions.success(platform, entity, mode),
    context,
  );
};

export const trackCabinetMutationError = (
  error: unknown,
  platform: CabinetPlatform,
  entity: CabinetEntity,
  mode: CabinetMode,
  context?: CabinetContext,
) => {
  datadogActions.trackDatadogError(normalizeError(error), {
    source: cabinetDatadogSources.mutationError(platform, entity, mode),
    ...context,
  });
};

interface CabinetMutationTrackingOptions<TData = void> {
  platform: CabinetPlatform;
  entity: CabinetEntity;
  mode: CabinetMode;
  onSuccess?: (data: TData) => void;
  onError?: (error: AppError) => void;
}

export const createCabinetMutationTracking = <TData = void>({
  platform,
  entity,
  mode,
  onSuccess,
  onError,
}: CabinetMutationTrackingOptions<TData>) => ({
  onSuccess: (data: TData) => {
    trackCabinetSubmitSuccess(platform, entity, mode);
    onSuccess?.(data);
  },
  onError: (error: AppError) => {
    trackCabinetMutationError(error, platform, entity, mode);
    onError?.(error);
  },
});
